import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Upload, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function CustomersImportExport({ onImported, disabled, startOpen = false }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { if (startOpen) setOpen(true); }, [startOpen]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [file, setFile] = useState(null);
  const [skipDup, setSkipDup] = useState(true);

  const dl = async (url, filename) => {
    try {
      const resp = await api.get(url, { responseType: "blob" });
      const u = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = u; a.download = filename; a.click();
      setTimeout(() => URL.revokeObjectURL(u), 1000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f); setLoading(true); setPreview(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/customers/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (err) { toast.error(formatApiError(err)); setFile(null); }
    finally { setLoading(false); }
  };

  const confirmImport = async () => {
    if (!file) return;
    setConfirming(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("skip_duplicates", skipDup ? "true" : "false");
      const { data } = await api.post("/customers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} cliente(s) importado(s). ${data.skipped} ignorado(s).`);
      onImported?.();
      setOpen(false); setFile(null); setPreview(null);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setConfirming(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setFile(null); setPreview(null); } }}>
      <DialogTrigger asChild>
        <Button data-testid="customers-io-btn" disabled={disabled}
          variant="outline" className="h-11 bg-zinc-900 border-zinc-800 text-zinc-200 hover:bg-zinc-800 disabled:opacity-50">
          <FileSpreadsheet className="h-4 w-4 mr-2" /> Importar / Exportar
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">Importar / Exportar clientes</DialogTitle>
          <DialogDescription className="text-zinc-400">
            CSV com cadastro completo. Opcional: uma linha pode trazer placa, marca, modelo e ano do veículo principal.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button data-testid="customers-export" onClick={() => dl("/customers/export.csv", "clientes.csv")} className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800">
              <Download className="h-4 w-4 mr-1" /> Exportar CSV
            </Button>
            <Button data-testid="customers-template" onClick={() => dl("/customers/import/template.csv", "modelo-clientes.csv")} variant="outline" className="bg-zinc-900 border-zinc-800">
              <Download className="h-4 w-4 mr-1" /> Baixar modelo
            </Button>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
            <label className="text-xs font-mono-caps text-zinc-400 flex items-center gap-2">
              <Upload className="h-4 w-4" /> Enviar CSV
            </label>
            <input data-testid="customers-import-file" type="file" accept=".csv,text/csv" onChange={onFile}
              className="mt-2 block w-full text-sm text-zinc-200 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-zinc-800 file:text-zinc-200" />
            {loading && <div className="mt-2 text-xs text-zinc-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Analisando…</div>}
          </div>
          {preview && (
            <div data-testid="customers-import-preview" className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
              <div className="flex items-center justify-between mb-2 text-xs">
                <span className="text-zinc-400">
                  Prévia: {preview.rows.length} linha(s) · <span className="text-amber-300" data-testid="dup-count">{preview.duplicates} duplicado(s)</span>
                </span>
                <label className="inline-flex items-center gap-1 text-zinc-300">
                  <input type="checkbox" checked={skipDup} onChange={(e) => setSkipDup(e.target.checked)} data-testid="skip-dup" /> Pular duplicados
                </label>
              </div>
              <div className="overflow-x-auto max-h-80">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="text-left text-zinc-500">
                      <th className="py-1 pr-2">#</th><th className="pr-2">Nome</th><th className="pr-2">E-mail</th>
                      <th className="pr-2">Doc.</th><th className="pr-2">Telefone</th><th className="pr-2">Placa</th>
                      <th className="pr-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={i} className={`border-t border-zinc-800 ${r.duplicate ? "text-amber-200" : r.error ? "text-red-300" : "text-zinc-200"}`}>
                        <td className="py-1 pr-2">{i + 1}</td>
                        <td className="pr-2">{r.name || "—"}</td>
                        <td className="pr-2">{r.email || "—"}</td>
                        <td className="pr-2">{r.document || "—"}</td>
                        <td className="pr-2">{r.phone || "—"}</td>
                        <td className="pr-2">{r.plate || "—"}</td>
                        <td className="pr-2">{r.duplicate ? `Duplicado (${r.duplicate_by})` : r.error ? `Erro: ${r.error}` : "OK"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={confirming} className="text-zinc-300">Fechar</Button>
          <Button data-testid="customers-import-confirm" onClick={confirmImport} disabled={!preview || confirming}
            className="bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase">
            {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmar importação"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
